/**
 * Tier-by-distance bands (HEX-64; extends the HEX-40 frontier-risk model).
 *
 * HEX-40/HEX-42 made the encounter *rate* scale with distance from the safe
 * zone; the *tier* stayed flat (a single 80/15/3/2 split everywhere), so
 * epics and legendaries could spawn right next to the origin. This module is
 * the single source of the tier→distance gradient consumed by both enemy
 * pickers:
 *
 *   - `ChunkedWorldGen.pickEnemyId` — pre-placed encounter tiles, keyed off
 *     `(q, r, seed)` deterministically.
 *   - `frontierRisk.pickLatentEnemy` — ground-cross latent mutations, keyed
 *     off the lifecycle PRNG.
 *
 * Both roll a tier from `tierWeightsAt(d)` then pick within-tier, via the
 * shared `pickTier` helper, so the "discover frontiers, danger grows outward"
 * thesis holds on every spawn path.
 */

import type { EnemyTier } from '../combat/types';

export type TierWeights = Readonly<Record<EnemyTier, number>>;

/**
 * Tier order, low→high. Cumulative weighting and the empty-tier walk-down both
 * rely on this ordering (fallback steps *down* to a less dangerous tier).
 */
export const TIER_ORDER = [
  'common',
  'rare',
  'epic',
  'legendary',
] as const satisfies readonly EnemyTier[];

/**
 * Band upper bounds, **exclusive**. Band `i` applies when `d < BAND_MAX[i]`;
 * a distance at or beyond the last bound uses the final weight row. Boundary
 * rule: a tile exactly on a bound belongs to the *outer* band (e.g. d = 30 is
 * band 1, not band 0).
 */
export const TIER_BAND_MAX_DISTANCES: readonly number[] = [30, 80, 160, 256];

/**
 * Tier weight rows by band (first-pass, tunable — HEX-64). Each row sums to
 * 100. Spawn neighbourhood is pure common; high tiers unlock only as the
 * player pushes out.
 *
 *     d   0– 30 → 100 /  0 /  0 /  0   (spawn region — always common)
 *     d  30– 80 →  75 / 25 /  0 /  0
 *     d  80–160 →  45 / 40 / 15 /  0
 *     d 160–256 →  20 / 40 / 30 / 10
 *     d 256+    →  10 / 30 / 35 / 25   (deep frontier — spans all tiers)
 */
const TIER_BAND_WEIGHTS: readonly TierWeights[] = [
  { common: 100, rare: 0, epic: 0, legendary: 0 },
  { common: 75, rare: 25, epic: 0, legendary: 0 },
  { common: 45, rare: 40, epic: 15, legendary: 0 },
  { common: 20, rare: 40, epic: 30, legendary: 10 },
  { common: 10, rare: 30, epic: 35, legendary: 25 },
];

/**
 * Band index (0..N) for a safe-zone distance from origin.
 * See `TIER_BAND_MAX_DISTANCES`.
 */
export function tierBand(d: number): number {
  for (let i = 0; i < TIER_BAND_MAX_DISTANCES.length; i++) {
    const cap = TIER_BAND_MAX_DISTANCES[i];
    if (cap !== undefined && d < cap) return i;
  }
  return TIER_BAND_MAX_DISTANCES.length;
}

/**
 * Tier weight distribution at a given safe-zone distance `d` from origin.
 * Distance from origin is the sole danger axis (ADR-0018 §1).
 */
export function tierWeightsAt(d: number): TierWeights {
  const row = TIER_BAND_WEIGHTS[tierBand(d)];
  // `tierBand` always returns a valid index (rows length = bands + 1), but the
  // tuple read is `| undefined` under noUncheckedIndexedAccess; pin the last
  // row as a defensive default.
  return row ?? (TIER_BAND_WEIGHTS[TIER_BAND_WEIGHTS.length - 1] as TierWeights);
}

/**
 * Pick a tier from a weight distribution given a roll in `[0, 1)`.
 *
 * Shared by both enemy pickers: the caller supplies the unit roll (from a
 * PRNG or a deterministic hash) and a non-empty predicate over the roster.
 *
 * Empty-tier fallback walks **down** to the nearest lower non-empty tier —
 * preserving HEX-40 behaviour (a band that would pick `legendary` with no
 * legendary registered degrades to `epic`, then `rare`, …). Throws only if no
 * tier has any registered enemy, which is a generator-time bug.
 */
export function pickTier(
  roll01: number,
  weights: TierWeights,
  isTierNonEmpty: (tier: EnemyTier) => boolean,
): EnemyTier {
  const total = TIER_ORDER.reduce((sum, t) => sum + weights[t], 0);
  const threshold = roll01 * total;
  let acc = 0;
  let picked: EnemyTier | null = null;
  let lastPositive: EnemyTier | null = null;
  for (const tier of TIER_ORDER) {
    if (weights[tier] > 0) lastPositive = tier;
    acc += weights[tier];
    if (threshold < acc) {
      picked = tier;
      break;
    }
  }
  // Numerical floor: roll01 === 1.0 (an injected PRNG can return it) or an
  // all-zero distribution leaves `picked` null. Fall back to the highest
  // positive-weight tier; the walk-down below then handles emptiness.
  if (picked === null) picked = lastPositive ?? TIER_ORDER[0];

  let tier: EnemyTier = picked;
  while (!isTierNonEmpty(tier)) {
    const idx = TIER_ORDER.indexOf(tier);
    if (idx <= 0) throw new Error('no enemies registered for any tier');
    tier = TIER_ORDER[idx - 1] as EnemyTier;
  }
  return tier;
}
